import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../lib/api";
import AdminLayout from "./AdminLayout";
import { toast, Toaster } from "sonner";
import { ArrowLeft, Users, Calendar, Trophy, Flame, CheckCircle2, Building2, UserPlus, X as XIcon } from "lucide-react";
import PhoneInput from "../../components/PhoneInput";
import { fullPhone, checkPhone } from "../../lib/validation";
import { ddmmyyyy, ddmmyyyyRange } from "../../lib/dates";

export default function AdminEventDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [event, setEvent] = useState(null);
    const [regs, setRegs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [savingFlag, setSavingFlag] = useState(false);
    const [regOpen, setRegOpen] = useState(false);
    const [regForm, setRegForm] = useState({ team_name: "", captain_name: "", captain_phone: "", members: "" });
    const [submitting, setSubmitting] = useState(false);

    const load = async () => {
        setLoading(true);
        const [{ data: ev }, { data: r }] = await Promise.all([
            api.get(`/events/${id}`),
            api.get(`/events/${id}/registrations`),
        ]);
        setEvent(ev);
        setRegs(r);
        setLoading(false);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    const toggleFewSpots = async () => {
        if (!event) return;
        setSavingFlag(true);
        try {
            const { data } = await api.put(`/events/${id}`, { few_spots_left: !event.few_spots_left });
            setEvent({ ...event, few_spots_left: data.few_spots_left });
            toast.success(data.few_spots_left ? "Marked as 'Few spots left'" : "Cleared 'Few spots left' badge");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        } finally {
            setSavingFlag(false);
        }
    };

    const submitManualReg = async (e) => {
        e.preventDefault();
        if (!regForm.team_name || regForm.team_name.length < 2) { toast.error("Team name required"); return; }
        if (!regForm.captain_name) { toast.error("Captain name required"); return; }
        if (!checkPhone(regForm.captain_phone).ok) { toast.error("Valid 10-digit phone required"); return; }
        setSubmitting(true);
        try {
            const members = regForm.members
                ? regForm.members.split(",").map(m => m.trim()).filter(Boolean)
                : [];
            await api.post(`/admin/events/${id}/register`, {
                team_name: regForm.team_name,
                captain_name: regForm.captain_name,
                captain_phone: fullPhone(regForm.captain_phone),
                members,
            });
            toast.success("Team registered");
            setRegOpen(false);
            setRegForm({ team_name: "", captain_name: "", captain_phone: "", members: "" });
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AdminLayout>
            <Toaster theme="dark" richColors position="top-center" />
            <div className="p-4 sm:p-8">
                <button onClick={() => navigate("/admin/events")} data-testid="back-events"
                    className="inline-flex items-center gap-2 text-slate-400 hover:text-[#00FF66] text-sm mb-4">
                    <ArrowLeft className="w-4 h-4" /> Back to events
                </button>

                {loading || !event ? (
                    <div className="text-slate-500">Loading...</div>
                ) : (
                    <>
                        <div className="flex items-start justify-between flex-wrap gap-4">
                            <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full bg-[#00FF66]/15 text-[#00FF66] border border-[#00FF66]/40">{event.sport}</span>
                                    {event.is_completed && (
                                        <span data-testid="detail-completed" className="inline-flex items-center gap-1 text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full bg-slate-700 text-slate-200 border border-slate-600">
                                            <CheckCircle2 className="w-3 h-3" /> Completed
                                        </span>
                                    )}
                                    {!event.is_completed && event.few_spots_left && (
                                        <span data-testid="detail-fewspots" className="inline-flex items-center gap-1 text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40">
                                            <Flame className="w-3 h-3" /> Few spots left
                                        </span>
                                    )}
                                </div>
                                <h1 className="font-display text-3xl font-bold uppercase text-white mt-3" data-testid="event-detail-title">{event.title}</h1>
                                <p className="text-slate-400 text-sm mt-1">{event.description}</p>
                            </div>

                            {!event.is_completed && (
                                <button onClick={toggleFewSpots} disabled={savingFlag}
                                    data-testid="toggle-fewspots"
                                    className={`inline-flex items-center gap-2 px-4 h-10 rounded text-xs font-bold uppercase tracking-widest border ${
                                        event.few_spots_left
                                            ? "bg-amber-500/20 text-amber-300 border-amber-500/50 hover:bg-amber-500/30"
                                            : "bg-slate-900 text-slate-300 border-slate-700 hover:border-amber-500/50"
                                    }`}>
                                    <Flame className="w-4 h-4" />
                                    {event.few_spots_left ? "Showing 'Few Spots Left'" : "Show 'Few Spots Left'"}
                                </button>
                            )}
                        </div>

                        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
                            <Stat icon={Calendar} label="Dates" value={ddmmyyyyRange(event.start_date, event.end_date)} />
                            <Stat icon={Building2} label="Centre" value={event.centre?.name || "—"} />
                            <Stat icon={Users} label="Registered" value={`${event.registrations_count} / ${event.max_teams}`} highlight />
                            <Stat icon={Trophy} label="Fee" value={`₹${event.registration_fee}`} />
                        </div>

                        <div className="mt-6 bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
                            <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between flex-wrap gap-2">
                                <div className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Registrations ({regs.length})</div>
                                {!event.is_completed && regs.length < event.max_teams && (
                                    <button onClick={() => setRegOpen(true)} data-testid="add-team-btn"
                                        className="inline-flex items-center gap-2 px-4 h-9 bg-[#00FF66] text-black text-xs font-bold uppercase tracking-widest rounded hover:bg-[#00cc52]">
                                        <UserPlus className="w-3 h-3" /> Register Team
                                    </button>
                                )}
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-slate-400 text-xs uppercase tracking-widest border-b border-slate-800 bg-slate-950/50">
                                            <th className="text-left py-3 px-4">Team</th>
                                            <th className="text-left py-3 px-4">Captain</th>
                                            <th className="text-left py-3 px-4">Phone</th>
                                            <th className="text-left py-3 px-4">Members</th>
                                            <th className="text-right py-3 px-4">Fee Paid</th>
                                            <th className="text-right py-3 px-4">Balance</th>
                                            <th className="text-left py-3 px-4">Status</th>
                                            <th className="text-left py-3 px-4">Date</th>
                                        </tr>
                                    </thead>
                                    <tbody className="text-slate-300">
                                        {regs.length === 0 ? (
                                            <tr><td colSpan={8} className="py-8 text-center text-slate-500" data-testid="no-registrations">No registrations yet.</td></tr>
                                        ) : regs.map(r => (
                                            <tr key={r.id} className="border-b border-slate-900" data-testid={`reg-${r.id}`}>
                                                <td className="py-3 px-4 text-white">{r.team_name}</td>
                                                <td className="py-3 px-4">{r.captain_name}</td>
                                                <td className="py-3 px-4">{r.captain_phone}</td>
                                                <td className="py-3 px-4 text-xs text-slate-400">{(r.members || []).join(", ") || "—"}</td>
                                                <td className="py-3 px-4 text-right font-bold text-[#00FF66]">₹{r.fee_paid}</td>
                                                <td className="py-3 px-4 text-right text-amber-300">₹{r.balance_due}</td>
                                                <td className="py-3 px-4 capitalize text-xs">{r.payment_status}</td>
                                                <td className="py-3 px-4 text-xs text-slate-500">{ddmmyyyy(r.created_at)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}

                {regOpen && (
                    <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-4">
                        <form onSubmit={submitManualReg} className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-lg p-6 space-y-3 relative" data-testid="manual-reg-form">
                            <button type="button" onClick={() => setRegOpen(false)} className="absolute top-3 right-3 text-slate-500 hover:text-white">
                                <XIcon className="w-5 h-5" />
                            </button>
                            <h3 className="font-display text-2xl font-bold uppercase text-white inline-flex items-center gap-2">
                                <UserPlus className="w-5 h-5 text-[#00FF66]" /> Register Team
                            </h3>
                            <p className="text-xs text-slate-400">Add a walk-in team to {event?.title}.</p>
                            <div>
                                <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Team Name</label>
                                <input value={regForm.team_name} onChange={(e) => setRegForm({ ...regForm, team_name: e.target.value })} required minLength={2}
                                    data-testid="reg-team-name"
                                    className="w-full mt-1 h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white" />
                            </div>
                            <div>
                                <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Captain Name</label>
                                <input value={regForm.captain_name} onChange={(e) => setRegForm({ ...regForm, captain_name: e.target.value })} required
                                    data-testid="reg-captain-name"
                                    className="w-full mt-1 h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white" />
                            </div>
                            <div>
                                <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Captain Phone</label>
                                <PhoneInput value={regForm.captain_phone} onChange={(v) => setRegForm({ ...regForm, captain_phone: v })} testid="reg-captain-phone" />
                            </div>
                            <div>
                                <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Members (comma separated)</label>
                                <textarea rows={2} value={regForm.members} onChange={(e) => setRegForm({ ...regForm, members: e.target.value })}
                                    placeholder="Rohit, Aryan, Karan, Vishal..."
                                    data-testid="reg-members"
                                    className="w-full mt-1 px-3 py-2 bg-slate-950 border border-slate-700 rounded text-white" />
                            </div>
                            <div className="bg-slate-950 border border-slate-700 rounded p-3 text-xs text-slate-400">
                                Booking fee: <span className="text-white font-bold">₹{event?.registration_fee}</span> · Advance auto-set to 50%.
                            </div>
                            <div className="flex gap-3 pt-2">
                                <button type="button" onClick={() => setRegOpen(false)} className="flex-1 h-11 border border-slate-700 rounded text-slate-300">Cancel</button>
                                <button type="submit" disabled={submitting} data-testid="save-reg-btn" className="flex-1 h-11 bg-[#00FF66] text-black font-bold uppercase tracking-widest rounded">
                                    {submitting ? "Registering..." : "Register Team"}
                                </button>
                            </div>
                        </form>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
}

function Stat({ icon: Icon, label, value, highlight }) {
    return (
        <div className={`bg-slate-900 border ${highlight ? "border-[#00FF66]/40" : "border-slate-800"} rounded-lg p-4`}>
            <div className="flex items-center justify-between">
                <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400">{label}</div>
                <Icon className={`w-4 h-4 ${highlight ? "text-[#00FF66]" : "text-slate-500"}`} />
            </div>
            <div className={`mt-2 font-display text-xl font-bold ${highlight ? "text-[#00FF66]" : "text-white"} truncate`}>{value}</div>
        </div>
    );
}
